/**
 * Daily and weekly challenges, with no server.
 *
 * ## How a window works
 *
 * When a window opens (the first time the app runs on a new day or week) it
 * picks its challenges and **snapshots every counter the pool uses**. From
 * then on a challenge's progress is simply `counter now − counter at open`.
 * Completing one is claiming it: its id goes into `done`, and the game banks
 * the XP as a counter of its own.
 *
 * ## Why everybody gets the same challenges on the same day
 *
 * The pick is a hash of the window key, not `Math.random()`. That makes it
 * stable across reloads without storing the dice, and it means two visitors
 * opening the game on the same day are working on the same thing —
 * the one piece of shared experience available without a backend. The only
 * per-player input is eligibility (`requires`/`until`), so a first-timer is
 * never handed "open a seed pack" before seed packs exist for them.
 *
 * ## What counts
 *
 * Anything that moves the counter after the window opened, including trees
 * that matured while the app was closed. That is deliberate: "grow three
 * trees today" is satisfied by coming back to a garden that grew overnight,
 * which rewards exactly the behaviour the game is built around.
 */

import { dayKey, startOfNextDay, startOfNextWeek, startOfWeek } from './calendar';
import { read, type Counters } from './counters';

export type Cadence = 'daily' | 'weekly';

export interface ChallengeDef<M extends string = string> {
  /** Stable and never reused — it is what `done` stores. */
  id: string;
  cadence: Cadence;
  metric: M;
  /** How far the counter has to move inside the window. */
  target: number;
  /** Banked on completion. */
  xp: number;
  title: string;
  titleAr: string;
  /** Offered only once every listed counter has reached its value. */
  requires?: Readonly<Partial<Record<M, number>>>;
  /** Offered only while every listed counter is still *below* its value. */
  until?: Readonly<Partial<Record<M, number>>>;
}

/** One open window. Plain data, so it can go straight into storage. */
export interface ChallengeWindow {
  key: string;
  ids: string[];
  baseline: Record<string, number>;
  done: string[];
}

export function windowKey(cadence: Cadence, now: number, weekStartsOn = 1): string {
  return cadence === 'daily'
    ? `d:${dayKey(now)}`
    : `w:${dayKey(startOfWeek(now, weekStartsOn))}`;
}

export function windowEndsAt(cadence: Cadence, now: number, weekStartsOn = 1): number {
  return cadence === 'daily' ? startOfNextDay(now) : startOfNextWeek(now, weekStartsOn);
}

/** FNV-1a. Not cryptographic — it only has to spread ids evenly. */
export function hash32(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function eligible<M extends string>(def: ChallengeDef<M>, counters: Counters<M>): boolean {
  for (const [metric, min] of Object.entries(def.requires ?? {}) as [M, number][]) {
    if (read(counters, metric) < min) return false;
  }
  for (const [metric, max] of Object.entries(def.until ?? {}) as [M, number][]) {
    if (read(counters, metric) >= max) return false;
  }
  return true;
}

/** Deterministic pick of up to `count` eligible challenges for a window. */
export function pickChallenges<M extends string>(
  pool: readonly ChallengeDef<M>[],
  cadence: Cadence,
  key: string,
  counters: Counters<M>,
  count: number,
): string[] {
  return pool
    .filter((def) => def.cadence === cadence && eligible(def, counters))
    .map((def) => ({ id: def.id, order: hash32(`${key}|${def.id}`) }))
    .sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : 1))
    .slice(0, Math.max(0, count))
    .map((entry) => entry.id);
}

function snapshot<M extends string>(
  pool: readonly ChallengeDef<M>[],
  counters: Counters<M>,
  into: Record<string, number> = {},
): Record<string, number> {
  for (const def of pool) {
    if (!(def.metric in into)) into[def.metric] = read(counters, def.metric);
  }
  return into;
}

/**
 * The window that should be open now: the current one if it is still today's
 * (or this week's), otherwise a fresh one.
 *
 * A window restored from storage is repaired rather than trusted: ids no
 * longer in the pool are dropped, and a metric the pool gained since the
 * snapshot is snapshotted now — otherwise its baseline would be missing and
 * that challenge could never make progress.
 */
export function rollWindow<M extends string>(
  current: ChallengeWindow | null,
  pool: readonly ChallengeDef<M>[],
  cadence: Cadence,
  now: number,
  counters: Counters<M>,
  count: number,
  weekStartsOn = 1,
): ChallengeWindow {
  const key = windowKey(cadence, now, weekStartsOn);
  if (current && current.key === key) {
    const known = new Set(pool.map((def) => def.id));
    return {
      key,
      ids: current.ids.filter((id) => known.has(id)),
      baseline: snapshot(pool, counters, { ...current.baseline }),
      done: current.done.filter((id) => known.has(id)),
    };
  }
  return {
    key,
    ids: pickChallenges(pool, cadence, key, counters, count),
    baseline: snapshot(pool, counters),
    done: [],
  };
}

export interface ChallengeProgress<M extends string = string> {
  def: ChallengeDef<M>;
  /** Clamped to `target`, so a bar never overflows. */
  value: number;
  done: boolean;
}

export function progressOf<M extends string>(
  window: ChallengeWindow,
  pool: readonly ChallengeDef<M>[],
  counters: Counters<M>,
): ChallengeProgress<M>[] {
  const byId = new Map(pool.map((def) => [def.id, def] as const));
  const out: ChallengeProgress<M>[] = [];
  for (const id of window.ids) {
    const def = byId.get(id);
    if (!def) continue;
    const done = window.done.includes(id);
    const base = window.baseline[def.metric] ?? read(counters, def.metric);
    const moved = Math.max(0, read(counters, def.metric) - base);
    out.push({ def, value: done ? def.target : Math.min(def.target, moved), done });
  }
  return out;
}

/** Challenges that have just reached their target and are not yet claimed. */
export function claimable<M extends string>(
  window: ChallengeWindow,
  pool: readonly ChallengeDef<M>[],
  counters: Counters<M>,
): ChallengeDef<M>[] {
  return progressOf(window, pool, counters)
    .filter((p) => !p.done && p.value >= p.def.target)
    .map((p) => p.def);
}

export function markDone(window: ChallengeWindow, ids: readonly string[]): ChallengeWindow {
  const done = new Set(window.done);
  for (const id of ids) if (window.ids.includes(id)) done.add(id);
  return { ...window, done: [...done] };
}

/** Sanitise a window read back from storage; anything malformed is null. */
export function parseWindow(raw: unknown): ChallengeWindow | null {
  if (!raw || typeof raw !== 'object') return null;
  const w = raw as Partial<ChallengeWindow>;
  if (typeof w.key !== 'string' || !Array.isArray(w.ids) || !Array.isArray(w.done)) return null;
  const strings = (xs: unknown[]) => xs.filter((x): x is string => typeof x === 'string');
  const baseline: Record<string, number> = {};
  if (w.baseline && typeof w.baseline === 'object') {
    for (const [k, v] of Object.entries(w.baseline)) {
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) baseline[k] = Math.floor(v);
    }
  }
  return { key: w.key, ids: strings(w.ids), baseline, done: strings(w.done) };
}
